import React from 'react'
import { Loader2, Mail, CalendarDays, ShieldCheck } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'

type Props = {
  connecting: boolean
  onConfirm: () => void
  onCancel: () => void
}

const CAPABILITIES = [
  {
    Icon: Mail,
    title: 'Read and send email',
    detail: 'Zeph reads your Outlook inbox and sends outreach, replies, and follow-ups from your own address — never a shared or generic sender.',
  },
  {
    Icon: CalendarDays,
    title: 'See your calendar',
    detail: 'Zeph reads your events and availability to show your schedule in the app, and to check your availability when scheduling meetings.',
  },
]

const OutlookWorkspaceConnectModal: React.FC<Props> = ({ connecting, onConfirm, onCancel }) => {
  const { currentTheme: t } = useTheme()
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={connecting ? undefined : onCancel} />
      <div
        className="relative z-10 w-full max-w-md mx-4 rounded-xl p-6 shadow-xl"
        style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}` }}
      >
        <h3 className="text-base font-semibold mb-1" style={{ color: t.text }}>Connect Outlook Workspace</h3>
        <p className="text-xs mb-5 leading-relaxed" style={{ color: t.mutedText }}>
          One Microsoft sign-in gives Zeph the two things it needs to work as your BDR co-worker:
        </p>

        <div className="flex flex-col gap-3 mb-5">
          {CAPABILITIES.map(({ Icon, title, detail }) => (
            <div key={title} className="flex gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: t.hoverBackground, border: `1px solid ${t.border}` }}
              >
                <Icon size={15} style={{ color: t.text }} />
              </div>
              <div>
                <p className="text-xs font-semibold" style={{ color: t.text }}>{title}</p>
                <p className="text-xs mt-0.5 leading-relaxed" style={{ color: t.mutedText }}>{detail}</p>
              </div>
            </div>
          ))}
        </div>

        <div
          className="flex gap-2.5 items-start px-3 py-2.5 rounded-lg mb-5"
          style={{ backgroundColor: t.hoverBackground }}
        >
          <ShieldCheck size={14} className="shrink-0 mt-0.5" style={{ color: t.mutedText }} />
          <p className="text-[11px] leading-relaxed" style={{ color: t.mutedText }}>
            Your tokens are encrypted and bound to your account — never visible to Zeph in plaintext or exposed to the browser.
            You can disconnect at any time from Settings → Integrations.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={connecting}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-80 disabled:opacity-50"
            style={{ backgroundColor: t.hoverBackground, border: `1px solid ${t.border}`, color: t.text }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={connecting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: t.accent }}
          >
            {connecting && <Loader2 size={13} className="animate-spin" />}
            Continue to Microsoft
          </button>
        </div>
      </div>
    </div>
  )
}

export default React.memo(OutlookWorkspaceConnectModal)
